import { useEffect, useState } from 'react';
import type { KeyboardEvent, ReactElement, ReactNode } from 'react';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  MessageBar,
  MessageBarBody,
  Spinner,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AppButton } from './AppButton';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
  warningList: {
    margin: 0,
    paddingLeft: tokens.spacingHorizontalL,
  },
  // Wider surface for dialogs with rich `content` (e.g. the grouped
  // empty-fields cards), so a multi-column grid has room to breathe.
  wideSurface: {
    maxWidth: '680px',
  },
});

export interface ConfirmActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: string;
  /**
   * Advisory warnings (e.g. unsaved changes) rendered as a bulleted list —
   * never blocks the Confirm button, purely informational.
   */
  warnings?: string[];
  /**
   * Arbitrary content rendered in the dialog body (below any warnings, above
   * the textarea). Lets callers supply richer, domain-specific UI — e.g. the
   * grouped empty-fields cards — without this generic dialog knowing about it.
   */
  content?: ReactNode;
  textarea?: {
    label?: string;
    placeholder?: string;
    required?: boolean;
  };
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: (value: string) => void;
  isPending?: boolean;
  /** Widen the dialog surface to fit rich `content` (e.g. a card grid). */
  wide?: boolean;
  /** Rendered inside a DialogTrigger — clicking it opens the dialog. */
  trigger?: ReactElement;
}

export function ConfirmActionDialog({
  open,
  onOpenChange,
  title,
  description,
  warnings,
  content,
  textarea,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  isPending = false,
  wide = false,
  trigger,
}: ConfirmActionDialogProps) {
  const styles = useStyles();
  const [value, setValue] = useState('');

  useEffect(() => {
    if (!open) setValue('');
  }, [open]);

  const isConfirmDisabled = isPending || (textarea?.required === true && !value.trim());
  const hasWarnings = warnings != null && warnings.length > 0;
  const hasContent =
    description != null || textarea != null || hasWarnings || content != null;

  // Enter confirms — but only when there's no textarea (there, Enter is for
  // newlines) and the focus isn't on an interactive control (so Enter on Cancel
  // still cancels). Esc-to-cancel is Fluent's built-in behavior.
  const handleKeyDown = (e: KeyboardEvent) => {
    if (textarea != null || e.key !== 'Enter' || isConfirmDisabled) return;
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'A') return;
    e.preventDefault();
    onConfirm(value);
  };

  const surface = (
    <DialogSurface className={wide ? styles.wideSurface : undefined} onKeyDown={handleKeyDown}>
      <DialogBody>
        <DialogTitle>{title}</DialogTitle>
        {hasContent && (
          <DialogContent>
            <div className={styles.content}>
              {description != null && <p>{description}</p>}
              {hasWarnings && (
                <MessageBar intent="warning">
                  <MessageBarBody>
                    <ul className={styles.warningList}>
                      {warnings!.map((warning) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  </MessageBarBody>
                </MessageBar>
              )}
              {content}
              {textarea != null && (
                <Field label={textarea.label}>
                  <Textarea
                    value={value}
                    onChange={(_e, data) => setValue(data.value)}
                    placeholder={textarea.placeholder}
                    resize="vertical"
                  />
                </Field>
              )}
            </div>
          </DialogContent>
        )}
        <DialogActions>
          <AppButton variant="secondary" onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </AppButton>
          <AppButton
            variant="primary"
            disabled={isConfirmDisabled}
            icon={isPending ? <Spinner size="tiny" /> : undefined}
            onClick={() => onConfirm(value)}
          >
            {confirmLabel}
          </AppButton>
        </DialogActions>
      </DialogBody>
    </DialogSurface>
  );

  if (trigger != null) {
    return (
      <Dialog open={open} onOpenChange={(_e, data) => onOpenChange(data.open)}>
        <DialogTrigger disableButtonEnhancement>{trigger}</DialogTrigger>
        {surface}
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(_e, data) => onOpenChange(data.open)}>
      {surface}
    </Dialog>
  );
}

export default ConfirmActionDialog;
